import React, { Fragment, useState } from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { HelpCircle as HelpCircleIcon } from 'react-feather'
import { isEmpty } from 'lodash'
import { Box, Button, Divider, Grid } from '@material-ui/core'
import { Alert, Skeleton } from '@material-ui/lab'

import {
  Container,
  ContentHeader,
  DriveTour,
  LoadingFeedback,
  NoDataMessages,
  Page,
  TicketInfo,
  TicketMessages,
} from 'components'
import { MessageForm, ResponsibleForm } from './components'

import helpers from 'helpers'

import useFetch from 'hooks/useFetch'
import usePermissions from 'hooks/usePermissions'

import theme from 'theme'
import useStyles from './styles'
import * as service from 'service'
import constants from 'constants/index'
import useTicketTableStyles from './components/TicketTable/styles'

const DataSubjectTicketDetails = ({ match }) => {
  const { ticketId } = match?.params

  const { COMPLETED_STATUS_ID, CANCELED_STATUS_ID } = constants.tickets
  const {
    DATA_SUBJECT_SHOW_STEPS_OBJECT,
    DATA_SUBJECT_SHOW_DRIVER_STEP_1,
    DATA_SUBJECT_SHOW_DRIVER_STEP_2,
    DATA_SUBJECT_SHOW_DRIVER_STEP_3,
    DATA_SUBJECT_SHOW_DRIVER_STEP_4,
    DATA_SUBJECT_SHOW_DRIVER_STEP_5,
  } = constants.tours.tickets

  const { messageSubTitle, getBoxMessageColor, getIconColor } =
    helpers.formatters.tickets

  const [openTour, setOpenTour] = useState(false)
  const [loading, setLoading] = useState(false)

  const classes = useStyles()
  const ticketTableClasses = useTicketTableStyles()
  const permissions = usePermissions()

  const { TITULAR_TICKETS } = constants.permissions

  const canInteract = permissions.permitted(TITULAR_TICKETS.INTERACT)

  const { response, isLoading, refresh } = useFetch(
    service.dponet.titularTickets.get,
    {
      ticketId,
    },
    [],
  )

  const ticket = response?.data?.ticket

  const {
    response: messagesResponse,
    isLoading: messagesIsLoading,
    refresh: refreshMessages,
  } = useFetch(
    service.dponet.titularTickets.indexMessages,
    {
      ticketId,
    },
    [],
  )

  const ticketMessages = messagesResponse?.data?.ticketMessages || []

  const { response: userCompaniesResponse, isLoading: userCompanyIsLoading } =
    useFetch(
      service.dponet.usersCompanies.list,
      { listByCompany: true, showSelf: true },
      [ticketId],
    )

  const userCompanies = userCompaniesResponse?.data?.userCompanies

  const ticketInfos = helpers.tickets.dataSubjectInfo(
    ticket,
    ticketTableClasses,
  )

  const isFinalized =
    ticket?.status?.id === COMPLETED_STATUS_ID ||
    ticket?.status?.id === CANCELED_STATUS_ID

  const handleRefresh = () => {
    refresh()
    refreshMessages()
  }

  const handleOpenTour = () => {
    const where = 'Solicitação de titulares'
    const params = { action: 'tour-visualizacao-solicitacao-titulares' }

    helpers.mixpanel.track(where, 'Tour', params)
    setOpenTour(true)
  }

  return (
    <Page title="Detalhes da solicitação">
      <Container>
        <ContentHeader title="Detalhes da solicitação" goBack>
          <Button
            variant="contained"
            color="primary"
            startIcon={<HelpCircleIcon size={20} />}
            onClick={handleOpenTour}
            id={DATA_SUBJECT_SHOW_DRIVER_STEP_1}
            fullWidth
          >
            Tutorial
          </Button>
        </ContentHeader>
        <LoadingFeedback open={loading} />
        <Box display="flex" flexDirection="column" gridGap={theme.spacing(3)}>
          <Grid container spacing={3}>
            <Grid
              item
              xs={12}
              md={8}
              component={Box}
              display="flex"
              flexDirection="column"
              gridGap={theme.spacing(2)}
              order={1}
              id={DATA_SUBJECT_SHOW_DRIVER_STEP_2}
            >
              <Box height="100%">
                <PerfectScrollbar
                  className={classes.containerMessages}
                  options={{
                    wheelPropagation: false,
                    useBothWheelAxes: true,
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    gridGap={theme.spacing(2)}
                    maxHeight={600}
                    height="100%"
                  >
                    <NoDataMessages isLoading={messagesIsLoading}>
                      <Fragment>
                        {!isEmpty(ticket?.attachments) && (
                          <Fragment>
                            <TicketMessages
                              title={ticket?.info?.questionAttachment?.title}
                              content={
                                ticket?.info?.questionAttachment?.description
                              }
                              attachments={ticket?.attachments}
                            />
                            <Divider />
                          </Fragment>
                        )}
                        {ticketMessages?.map((message, index) => {
                          const subTitle = messageSubTitle(message)
                          const boxColor = getBoxMessageColor(message)
                          const iconColor = getIconColor(message)

                          return (
                            <TicketMessages
                              key={message.id || index}
                              title={message.author?.name}
                              subTitle={subTitle}
                              content={message?.content}
                              attachments={message?.attachments}
                              author={message?.author?.name}
                              sourceType={message?.sourceType}
                              interactions={message?.ticketMessageInteractions}
                              boxColor={boxColor}
                              iconColor={iconColor}
                            />
                          )
                        })}
                      </Fragment>
                    </NoDataMessages>
                  </Box>
                </PerfectScrollbar>
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              component={Box}
              display="flex"
              flexDirection="column"
              gridGap={theme.spacing(3)}
              order={2}
            >
              {userCompanyIsLoading ? (
                <Skeleton variant="rect" width="100%" height={97} />
              ) : (
                <ResponsibleForm
                  id={DATA_SUBJECT_SHOW_DRIVER_STEP_3}
                  ticket={ticket}
                  refresh={handleRefresh}
                  setLoading={setLoading}
                  userCompanies={userCompanies}
                  disabled={isFinalized || !canInteract}
                />
              )}
              <TicketInfo
                id={DATA_SUBJECT_SHOW_DRIVER_STEP_4}
                isLoading={isLoading}
                items={ticketInfos}
              />
            </Grid>
          </Grid>
          {!isLoading && (
            <Fragment>
              <Alert severity="info" variant="outlined">
                Todos os arquivos enviados pelo titular possuem um prazo de
                validade de 30 dias. Após esse período, os arquivos serão
                excluídos automaticamente da base e não poderão ser visualizados
                novamente.
              </Alert>
              <MessageForm
                id={DATA_SUBJECT_SHOW_DRIVER_STEP_5}
                ticketId={ticketId}
                ticket={ticket}
                setLoading={setLoading}
                refresh={handleRefresh}
                disabled={isFinalized || !canInteract}
              />
            </Fragment>
          )}
        </Box>
        <DriveTour
          stepsMatrix={DATA_SUBJECT_SHOW_STEPS_OBJECT}
          open={openTour}
          setOpen={setOpenTour}
        />
      </Container>
    </Page>
  )
}

export default DataSubjectTicketDetails
